// @/panels/ShapesPanelWithContexts.tsx
// Context-driven version of ShapesPanel with no prop drilling
// Eliminates 18 props by using context hooks

import React, { useEffect, useMemo, useState } from "react";
import { Shape, Component, UnifiedElement } from "@/types/diagram";
import { CirclePlus, ArrowLeft, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import EditElementDialog from "../elements/edit-element-dialog";
import CategoriesPanel from "./categories-panel";
import { Badge } from "@/components/ui/badge";
import clsx from "clsx";
import { CUSTOM_SCROLLBAR, PAGE_SIZE } from "@/lib/constants";
import { formatString } from "@/lib/utils";
import CustomTooltip from "@/components/flow/custom-tooltip";

// Import context hooks
import SVGPreview from "../ui/svg-preview";
import { useLibrary } from "@/context/library-context";
import { useDiagram } from "@/context/diagram-context";
import { componentLibraryManager } from "@/lib/component.lib";
import {
  DiagramLoader,
  ShapesLoadingSkeleton,
} from "../elements/loader-skeleton";

type ElementType = "3D" | "2D" | "LAYERS" | "COMPONENT";

interface ShapesPanelWithContextsProps {}

const filterOptionsWithColor = [
  { name: "All", color: "" },
  { name: "Primitives", color: "bg-primitive" },
  { name: "Modifiers", color: "bg-modifier" },
  { name: "Composites", color: "bg-composite" },
];
const filterBgColors = {
  "3D": "bg-primitive",
  LAYERS: "bg-primitive",
  "2D": "bg-modifier",
  COMPONENT: "bg-composite",
};

const showTypeName = {
  "3D": "3D",
  LAYERS: "Layer",
  "2D": "2D",
  COMPONENT: "Component",
};

const ShapesTab: React.FC<ShapesPanelWithContextsProps> = () => {
  // Access contexts instead of props
  const diagram = useDiagram();
  const library = useLibrary();
  const { ensureLibrary } = library;
  useEffect(() => {
    ensureLibrary();
  }, [ensureLibrary]);

  // Local component state
  const [inputQuery, setInputQuery] = useState("");
  const [selectedFilter, setselectedFilter] = useState("All");
  const [isEditDialog, setIsEditDialog] = useState(false);
  const [currentShapeDetails, setCurrentShapeDetails] = useState<
    Shape | Component | UnifiedElement | null
  >(null);

  useEffect(() => {
    const updatedCurrentComponentDetails = library.components.find(
      (component) => component.name === currentShapeDetails?.name
    );
    if (updatedCurrentComponentDetails) {
      setCurrentShapeDetails(updatedCurrentComponentDetails);
    }

    const updatedCurrentShapeDetails = library.shapesByCategory.find(
      (shape) => shape.name === currentShapeDetails?.name
    );
    if (updatedCurrentShapeDetails) {
      setCurrentShapeDetails(updatedCurrentShapeDetails);
    }
  }, [library.components, library.shapesByCategory]);

  const isAddDisabled: Record<ElementType, boolean> = {
    "3D":
      diagram.diagramComponents.length > 0 && diagram.selectedShape === null,
    LAYERS:
      diagram.diagramComponents.length > 0 && diagram.selectedShape === null,
    "2D": diagram.selectedShape === null,
    COMPONENT:
      diagram.diagramComponents.length > 0 && diagram.selectedShape === null,
  };

  const addActionFor: Record<ElementType, (arg: any) => void> = {
    "3D": (shape: Shape) => diagram.add3DShape(shape.name, library.svgLibrary),
    LAYERS: (shape: Shape) =>
      diagram.add3DShape(shape.name, library.svgLibrary),
    "2D": (shape: Shape) =>
      diagram.add2DShape(shape.name, shape.attachTo ?? "", library.svgLibrary),
    COMPONENT: (component: Component) => diagram.addComponent(component),
  };

  // Temporary function to show SVG preview content for a component
  const getComponentPreview = (component: Component): string => {
    if (!component.svgContent || component.svgContent === "") {
      return componentLibraryManager.renderComponent(
        component.id,
        diagram.canvasSize,
        library.svgLibrary,
        component
      );
    }
    return component.svgContent;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInputQuery("");
    setCurrentShapeDetails(null);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setselectedFilter("All");
      library.setSearchQuery(inputQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [inputQuery]);

  const isAllOrComponent =
    selectedFilter === "All" ||
    selectedFilter.toLocaleUpperCase() === "COMPOSITES";

  const { isSearchLoading, isShapesLoading } = library.isDataLoading;

  const filteredShapes = useMemo(() => {
    const upperCaseFilter = selectedFilter.toLocaleUpperCase();

    const filterMap: Record<string, string[] | null> = {
      ALL: null, // Show all shapes
      COMPOSITES: [], // Return empty array
      PRIMITIVES: ["3D", "LAYERS"],
      MODIFIERS: ["2D"],
    };

    const allowedTypes = filterMap[upperCaseFilter];

    return allowedTypes === null
      ? library.shapesByCategory
      : library.shapesByCategory.filter((shape) =>
          allowedTypes.includes(shape.type.toLocaleUpperCase())
        );
  }, [selectedFilter, library.shapesByCategory]);

  const groupedSearchedElements = useMemo(() => {
    const upperCaseFilter = selectedFilter.toLocaleUpperCase();

    const filterMap: Record<string, string[]> = {
      COMPOSITES: ["COMPONENT"],
      PRIMITIVES: ["3D", "LAYERS"],
      MODIFIERS: ["2D"],
    };

    const allowedTypes = filterMap[upperCaseFilter] ?? null;

    const filteredData =
      allowedTypes === null
        ? library.searchedData?.data ?? []
        : (library.searchedData?.data ?? []).filter((shape) =>
            allowedTypes.includes(shape.type.toLocaleUpperCase())
          );

    const groupedElements = filteredData.reduce((acc, element) => {
      const pathKey = element.path?.split("/").join(" / ") || "unknown";
      (acc[pathKey] ||= []).push(element);
      return acc;
    }, {} as Record<string, UnifiedElement[]>);

    return { data: groupedElements, total: filteredData.length };
  }, [library.searchedData, selectedFilter]);

  const handleCategoryChange = (id: string) => {
    setCurrentShapeDetails(null);
    library.setActiveCategory(id);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === "") {
      setCurrentShapeDetails(null);
    }
    setInputQuery(e.target.value);
  };

  const renderPreview = (element: UnifiedElement) => (
    <SVGPreview
      svgContent={
        ("type" in element && (element.type === "2D" || element.type === "3D")
          ? element.svgContent
          : getComponentPreview(element as Component)) ?? ""
      }
      className="w-full h-full object-cover bg-white"
    />
  );

  const renderElement = (element: Shape | Component) => {
    const elementType = "type" in element ? element.type : "COMPONENT";

    // Define colors based on element type

    const handlePreviewClick = (e: React.MouseEvent, type: ElementType) => {
      e.stopPropagation();
      if (!isAddDisabled[type]) {
        addActionFor[type](element);
      }
    };

    const handleElementClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setCurrentShapeDetails(element);
    };

    return (
      <CustomTooltip
        key={`${element.name}-${element.version}`}
        action={
          <div
            onClick={handleElementClick}
            className="flex flex-col  p-2 bg-surface-card cursor-pointer rounded-lg  relative aspect-[3/2]  disabled:opacity-80 disabled:cursor-not-allowed hover:bg-surface-hover"
          >
            <div
              role="button"
              aria-disabled={isAddDisabled[elementType]}
              onClick={(e) => handlePreviewClick(e, elementType)}
              className={`relative w-full h-full ${
                isAddDisabled[elementType]
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer"
              }`}
            >
              {renderPreview(element as UnifiedElement)}

              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm opacity-0 hover:opacity-100 transition-opacity">
                <CirclePlus className="text-white text-3xl select-none" />
              </div>
            </div>
            <div className="flex items-start gap-2 mt-2 ">
              <div
                className={clsx(
                  `w-2 h-2 ${
                    filterBgColors[
                      elementType.toLocaleUpperCase() as keyof typeof filterBgColors
                    ]
                  } rounded-full shrink-0 mt-1.5`
                )}
              />
              <p className="h-10 break-words text-white text-sm overflow-hidden text-ellipsis  line-clamp-2">
                {formatString(element.name)}
              </p>
            </div>
          </div>
        }
        header={formatString(element.name)}
        side="bottom"
      />
    );
  };

  const renderShapeDetails = (element: UnifiedElement) => {
    const elementType = "type" in element ? element.type : "COMPONENT";

    return (
      <div className="p-4 bg-surface-card rounded-lg">
        <div className="flex gap-3">
          <div className="w-[106px] h-[106px] flex-shrink-0">
            {renderPreview(element as UnifiedElement)}
          </div>
          <div className="flex gap-2 flex-col">
            <div className="flex items-center gap-2 mt-2 ">
              <div
                className={clsx(
                  `w-3 h-3 ${
                    filterBgColors[
                      elementType.toUpperCase() as keyof typeof filterBgColors
                    ]
                  } rounded-full shrink-0`
                )}
              />
              <p className="text-white text-base overflow-hidden text-ellipsis whitespace-pre-line line-clamp-1">
                {formatString(element.name)}
              </p>
            </div>
            <div className="flex gap-2 text-sm">
              <Badge
                variant="secondary"
                className="flex items-center w-fit bg-surface-active px-2 py-1"
              >
                <span>v{element.version}</span>
              </Badge>
              <Badge
                variant="secondary"
                className="flex items-center w-fit bg-surface-active px-2 py-1"
              >
                <span>{showTypeName[elementType]}</span>
              </Badge>
            </div>
            <p className="text-muted text-sm">
              {element.description
                ? element.description
                : "No description available."}
            </p>
          </div>
        </div>
        <div className="flex gap-2 justify-end ">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              setCurrentShapeDetails(element);

              setIsEditDialog(true);
            }}
            className="flex gap-2 text-sm text-button-text bg-primary border-primary hover:bg-primary-dark hover:text-button-text"
          >
            Edit
          </Button>
          <Button
            disabled={isAddDisabled[elementType]}
            onClick={(e) => {
              e.stopPropagation();
              addActionFor[elementType](element);
            }}
            className="flex gap-2 text-sm text-button-text bg-primary border-primary hover:bg-primary-dark hover:text-button-text disabled:opacity-50 disabled:text-button-text"
          >
            Add
          </Button>
        </div>
      </div>
    );
  };

  return (
    <main className="p-4 h-full flex flex-col gap-4">
      <div>
        <div className="flex items-center bg-surface-input rounded-md gap-2 border-surface-border border-2 border-solid">
          <input
            value={inputQuery}
            autoComplete="off"
            onChange={handleInputChange}
            type="text"
            name="search"
            placeholder="Search"
            className="w-full p-2 bg-surface-input text-white placeholder-muted rounded focus:outline-none"
          />
          {inputQuery && (
            <button
              onClick={handleSubmit}
              className="bg-surface-active rounded p-1 hover:bg-surface-hover"
            >
              <X size={12} />
            </button>
          )}
          <button className="p-2 hover:bg-surface-hover rounded">
            <Search size={20} />
          </button>
        </div>
        <div className="flex mt-2 flex-wrap justify-end gap-1 ">
          {filterOptionsWithColor.map((item) => (
            <button
              key={item.name}
              className={`flex items-center gap-1 rounded px-2 py-1 focus:outline-none ${
                selectedFilter === item.name
                  ? "bg-surface-hover text-text-primary dark:bg-blue-900/60 dark:text-white hover:bg-surface-hover dark:hover:bg-blue-900/70"
                  : "bg-surface hover:bg-surface-hover"
              }`}
              onClick={() => setselectedFilter(item.name)}
            >
              {item.color && (
                <div
                  className={clsx(
                    `w-3 h-3 ${item.color} rounded-full shrink-0`
                  )}
                />
              )}
              <div className="relative ">
                {/* Hidden reference text */}
                <span
                  aria-hidden="true"
                  className={clsx(
                    "invisible block whitespace-nowrap text-xs",
                    "font-normal text-lightGray2"
                  )}
                >
                  {item.name}
                </span>

                {/* Visible text */}
                <span
                  className={clsx(
                    "absolute left-0 top-1/2 -translate-y-1/2 text-xs",
                    "font-normal text-lightGray2"
                  )}
                >
                  {item.name}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {inputQuery && (
        <section
          className={`flex-grow overflow-auto bg-surface-input px-4 py-3 rounded-lg ${CUSTOM_SCROLLBAR}`}
        >
          {groupedSearchedElements.total} Result Found
          {isSearchLoading
            ? Array.from({ length: PAGE_SIZE }).map((_, index) => (
                <DiagramLoader key={index} />
              ))
            : groupedSearchedElements.data &&
              Object.entries(groupedSearchedElements.data).map(
                ([path, group]) => (
                  <div key={path}>
                    <h2 className="my-3 text-sm">
                      / {path.split("/").slice(1).slice(-2).join(" / ")}
                    </h2>
                    <ul>
                      {group.map((element) => (
                        <li key={element.name} className="mt-3">
                          {renderShapeDetails(element as UnifiedElement)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              )}
        </section>
      )}
      {!inputQuery && library.categories.length > 0 && (
        <CategoriesPanel
          categories={library.categories}
          onCategoryChange={handleCategoryChange}
        />
      )}

      {!inputQuery && !currentShapeDetails && (
        <section className={`flex-grow overflow-auto ${CUSTOM_SCROLLBAR}`}>
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
            {isShapesLoading
              ? Array.from({ length: PAGE_SIZE }).map((_, index) => (
                  <ShapesLoadingSkeleton key={index} />
                ))
              : library.shapesByCategory.length > 0 ||
                library.components.length > 0
              ? [
                  ...(isAllOrComponent
                    ? library.components.map((element) =>
                        renderElement(element)
                      )
                    : []),
                  ...(selectedFilter === "All" || filteredShapes.length > 0
                    ? filteredShapes.map((element) => renderElement(element))
                    : []),
                ]
              : null}
          </div>
        </section>
      )}

      {!inputQuery && currentShapeDetails && (
        <section className={`flex-grow overflow-auto ${CUSTOM_SCROLLBAR}`}>
          <button
            onClick={() => setCurrentShapeDetails(null)}
            className="flex gap-2 text-base items-center"
          >
            <ArrowLeft size={16} />
            <span> Back</span>
          </button>
          {renderShapeDetails(currentShapeDetails as UnifiedElement)}
        </section>
      )}
      {currentShapeDetails?.name && (
        <EditElementDialog
          isOpen={isEditDialog}
          onClose={() => {
            setCurrentShapeDetails(null);
            setIsEditDialog(false);
          }}
          element={currentShapeDetails as UnifiedElement}
        />
      )}
    </main>
  );
};

export default ShapesTab;
